/**
 * MultiResponse — checkbox list for selecting zero or more options.
 *
 * Value is an array of option keys. Commits on submit-button click.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { ResponseCoreProps, ResponseOption } from './types';
import './responses.css';

interface Props extends ResponseCoreProps<string[]> {
  options: ResponseOption[];
  /** Minimum number of selections required to enable submit. Default: 1. */
  minSelections?: number;
}

export function MultiResponse({
  label,
  hideLabel,
  initialValue = null,
  onChange,
  onSubmit,
  disabled,
  autoFocus,
  hideSubmit,
  submitLabel = 'Send',
  helper,
  options,
  minSelections = 1,
}: Props) {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(initialValue ?? []),
  );
  const firstBoxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) firstBoxRef.current?.focus();
  }, [autoFocus]);

  function toggle(key: string) {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
    onChange?.(Array.from(next));
  }

  function commit(e?: FormEvent) {
    if (e) e.preventDefault();
    if (selected.size < minSelections) return;
    onSubmit?.(Array.from(selected));
  }

  return (
    <form className="cf-response cf-multi-response" onSubmit={commit}>
      <fieldset className="cf-response-fieldset" disabled={disabled}>
        <legend className={hideLabel ? 'cf-visually-hidden' : 'cf-response__label'}>
          {label}
        </legend>
        {options.map((opt, i) => (
          <label
            key={opt.key}
            className={
              selected.has(opt.key)
                ? 'cf-response-option cf-response-option--selected'
                : 'cf-response-option'
            }
          >
            <input
              ref={i === 0 ? firstBoxRef : undefined}
              type="checkbox"
              value={opt.key}
              checked={selected.has(opt.key)}
              onChange={() => toggle(opt.key)}
              className="cf-response-option__native"
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </fieldset>
      {helper && <div className="cf-response__helper">{helper}</div>}
      {!hideSubmit && (
        <div className="cf-response__actions">
          <button
            type="submit"
            className="cf-btn cf-btn--primary"
            disabled={disabled || selected.size < minSelections}
          >
            {submitLabel}
          </button>
        </div>
      )}
    </form>
  );
}
